<template>
    <section class="pc-card">
        <!-- Header -->
        <div class="pc-header">
            <i class="fal fa-sticky-note text-blue-500"></i>
            <h2 class="text-lg leading-6 font-medium text-blue-500 mb-0">
                {{ $t('post.page.comments.internal_note') }}
            </h2>
            <span class="pc-badge">{{ post.comments.length }}</span>
        </div>

        <!-- Comments list -->
        <div class="pc-list-wrap">
            <ul v-if="post.comments && post.comments.length > 0" class="pc-list">
                <li v-for="comment in post.comments" :key="comment.id" class="pc-item">
                    <div class="pc-avatar">
                        <i class="fal fa-user"></i>
                    </div>
                    <div class="pc-content">
                        <div class="pc-author">
                            <strong>{{ comment.user.name }}</strong>
                            <span class="pc-date">
                                {{ $t('post.page.comments.the') }}
                                {{ moment(comment.created_at).locale($store.state.lang).format('L') }}
                                {{ $t('post.page.comments.at') }}
                                {{ moment(comment.created_at).locale($store.state.lang).format('HH[h]mm') }}
                            </span>
                        </div>
                        <div v-if="comment.message" class="pc-message">
                            <span
                                id="comments"
                                class="break-words"
                                v-if="comment.message.message"
                                v-html="displayHtml(comment.message.message)"
                                style="white-space: pre-line"
                            ></span>
                            <div v-if="comment.message.files" class="pc-files">
                                <a
                                    v-for="(file, index) in comment.message.files"
                                    :key="index"
                                    :href="file.url"
                                    :download="file.name"
                                    class="pc-file-link"
                                >
                                    <i class="fal fa-paperclip"></i> {{ file.name }}
                                </a>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>

            <!-- Empty -->
            <div v-else class="pc-empty">
                <i class="fal fa-sticky-note"></i>
                <p>{{ $t('post.page.comments.no_comment') }}</p>
            </div>
        </div>

        <!-- Compose -->
        <div class="pc-compose">
            <div id="ckComments" class="pc-editor">
                <ckeditor
                    v-model="content"
                    :config="editorConfig"
                    :editor="editor"
                    @ready="onEditorReady"
                    @input="onChange()"
                />
                <MentionsDropdown
                    :open="mentionState.open"
                    :users="mentionState.users"
                    :selected-index="mentionState.selectedIndex"
                    :position="mentionState.position"
                    :loading="mentionState.loading"
                    :query="mentionState.query"
                    @select="selectMention"
                    @hover="onMentionHover"
                />
            </div>
            <div class="pc-compose-actions">
                <button
                    v-if="!loading"
                    :disabled="isDisabled"
                    :class="{ 'pc-btn-disabled': isDisabled }"
                    class="pc-btn pc-btn-send"
                    type="button"
                    @click.prevent="postComment"
                >
                    <i class="fal fa-paper-plane text-white"></i>
                    <span class="hidden sm:inline">{{ $t('post.page.comments.write') }}</span>
                </button>
                <button v-else class="pc-btn pc-btn-send" disabled>
                    <span class="spinner-border spinner-border-sm" role="status"></span>
                    {{ $t('post.page.comments.process') }}
                </button>

                <label v-if="!loading" class="pc-file-label inline-flex items-center justify-center">
                    <input
                        type="file"
                        multiple
                        class="pc-file-input"
                        @change="onFileInput"
                    />
                    <span class="pc-btn pc-btn-attach">
                        <i class="fal fa-cloud-upload text-white"></i>
                        <span class="hidden sm:inline">{{ $t('post.page.messages.add_attachments') }}</span>
                    </span>
                    <span class="pc-file-count" v-if="filesList.length > 0">
                        {{ filesList.length }} <i class="fal fa-paperclip"></i>
                    </span>
                </label>
            </div>
        </div>
    </section>
</template>

<style>
#comments > ul li,
#comments > ul {
    list-style-type: disc !important;
}
#comments > ul {
    padding-left: 15px !important;
}
#comments > ol li,
#comments > ol {
    list-style-type: number !important;
}
#comments > ol {
    padding-left: 15px !important;
}

/* Mention chip — applied while typing inside CKEditor (.ck-content) and on
 * the read-only render of saved comments (.pc-message). The href #mention-X
 * pattern is our own marker, never a real navigation target. */
.ck-content a[href^="#mention-"],
.pc-message a[href^="#mention-"] {
    color: var(--v2-primary, #0891b2);
    background: rgba(8, 145, 178, 0.1);
    padding: 1px 5px;
    border-radius: 4px;
    text-decoration: none !important;
    font-weight: 600;
}

.pc-message a[href^="#mention-"] {
    pointer-events: none;
    cursor: default;
}
</style>

<style scoped>
.pc-file-label {
    cursor: pointer;
    margin: 0;
}

.pc-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.pc-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.pc-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
}

.pc-header > i {
    font-size: 1rem;
    flex-shrink: 0;
}

.pc-badge {
    margin-left: auto;
    font-size: 0.72rem;
    font-weight: 700;
    color: #3b82f6;
    background: #eff6ff;
    padding: 2px 10px;
    border-radius: 20px;
}

/* List */
.pc-list-wrap {
    max-height: 500px;
    overflow-y: auto;
}

.pc-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.pc-item {
    display: flex;
    gap: 14px;
    padding: 16px 24px;
}

.pc-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #f1f5f9;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    flex-shrink: 0;
}

.pc-content {
    flex: 1;
    min-width: 0;
}

.pc-author {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 6px;
}

.pc-author strong {
    font-size: 0.85rem;
    color: #1e293b;
}

.pc-date {
    font-size: 0.72rem;
    color: #3b82f6;
    font-weight: 500;
}

.pc-message {
    font-size: 0.88rem;
    color: #475569;
    line-height: 1.65;
    word-break: break-word;
}

.pc-files {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.pc-file-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    color: #3b82f6;
    text-decoration: none !important;
}

.pc-file-link:hover {
    text-decoration: underline !important;
}

/* Empty */
.pc-empty {
    text-align: center;
    padding: 40px 20px;
    color: #94a3b8;
}

.pc-empty i {
    font-size: 2rem;
    display: block;
    margin-bottom: 10px;
    color: #cbd5e1;
}

/* Compose */
.pc-compose {
    border-top: 1px solid #f1f5f9;
    padding: 16px 24px;
    background: #fafbfc;
}

.pc-editor {
    margin-bottom: 12px;
}

::v-deep .pc-editor .ck.ck-editor {
    border: 1px solid #e5e7eb !important;
    border-radius: 10px !important;
}

::v-deep .pc-editor .ck.ck-toolbar {
    border: none !important;
    border-bottom: 1px solid #e5e7eb !important;
    border-radius: 10px 10px 0 0 !important;
    background: #fff !important;
}

::v-deep .pc-editor .ck.ck-editor__main > .ck-editor__editable {
    border: none !important;
    border-radius: 0 0 10px 10px !important;
    padding: 12px 16px !important;
    min-height: 80px;
    color: #000 !important;
}

.pc-compose-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.pc-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 20px;
    border-radius: 10px;
    font-size: 0.82rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    color: #fff;
    white-space: nowrap;
}

.pc-btn i {
    color: #fff !important;
}

.pc-btn-send {
    background: #3b82f6;
}

.pc-btn-send:hover {
    background: #2563eb;
}

.pc-btn-disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pc-btn-attach {
    background: #6366f1;
}

.pc-btn-attach:hover {
    background: #4f46e5;
}

.pc-file-count {
    font-size: 0.82rem;
    color: #64748b;
    margin-left: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
}

::v-deep .upload .upload-draggable {
    padding: 0;
    padding-right: 3px;
    border: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>

<script>
import { mapActions, mapState } from 'vuex'
import axios from '@/config/backend'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import UploadAdapter from '@/components/Model/File/UploadAdapter'
import MentionsDropdown from '@/components/Model/Post/MentionsDropdown.vue'

export default {
    name: 'PostComments',
    components: {
        MentionsDropdown
    },
    computed: {
        ...mapState('post', {
            post: (state) => state.post
        }),
        isDisabled() {
            if (
                (this.content != null && this.content != '') ||
                (this.filesList != [] && this.filesList.length > 0)
            ) {
                return false
            }
            return true
        }
    },
    data() {
        return {
            content: null,
            loading: false,
            editor: ClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'bold',
                        'italic',
                        'link',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'undo',
                        'redo',
                        '|'
                    ]
                }
            },
            filesList: [],
            dropFiles: [],
            editorInstance: null,
            mentionSearchTimer: null,
            mentionState: {
                open: false,
                users: [],
                selectedIndex: 0,
                position: { left: 0, top: 0 },
                loading: false,
                query: '',
                atOffset: 0,
                block: null
            }
        }
    },
    created() {},
    beforeDestroy() {
        if (this.mentionSearchTimer) clearTimeout(this.mentionSearchTimer)
    },
    methods: {
        ...mapActions('post', ['sendNewComment', 'getPostHistories']),
        onEditorReady(editor) {
            this.editorInstance = editor
            this.uploader(editor)
            this.setupMentionListeners(editor)
        },
        setupMentionListeners(editor) {
            editor.model.document.on('change:data', () => {
                // Detect @-context after every text change.
                this.refreshMentionContext()
            })
            // Keyboard nav while the dropdown is open — high priority so we
            // intercept Enter/Esc before the editor inserts a newline.
            editor.editing.view.document.on(
                'keydown',
                (evt, data) => {
                    if (!this.mentionState.open) return
                    const key = data.domEvent.key
                    if (key === 'ArrowDown') {
                        this.moveMentionSelection(1)
                        evt.stop(); data.preventDefault()
                    } else if (key === 'ArrowUp') {
                        this.moveMentionSelection(-1)
                        evt.stop(); data.preventDefault()
                    } else if (key === 'Enter' || key === 'Tab') {
                        const u = this.mentionState.users[this.mentionState.selectedIndex]
                        if (u) {
                            this.selectMention(u)
                            evt.stop(); data.preventDefault()
                        }
                    } else if (key === 'Escape') {
                        this.closeMentionDropdown()
                        evt.stop(); data.preventDefault()
                    }
                },
                { priority: 'high' }
            )
        },
        refreshMentionContext() {
            const ctx = this.detectMentionContext()
            if (!ctx) {
                if (this.mentionState.open) this.closeMentionDropdown()
                return
            }
            this.mentionState.atOffset = ctx.atOffset
            this.mentionState.block = ctx.block
            this.mentionState.query = ctx.query
            this.mentionState.open = true
            // Wait for CKEditor + the browser to paint the new character before
            // measuring the cursor — otherwise the view→DOM mapping returns a
            // stale (or empty) rect and the dropdown lands at 0,0.
            requestAnimationFrame(() => {
                if (!this.mentionState.open) return
                this.mentionState.position = this.cursorScreenPosition()
            })
            this.queueMentionSearch(ctx.query)
        },
        detectMentionContext() {
            const editor = this.editorInstance
            if (!editor) return null
            const sel = editor.model.document.selection
            const pos = sel.getFirstPosition()
            if (!pos || !sel.isCollapsed) return null
            const block = pos.parent
            if (!block) return null
            // Build the plain-text content of the block from start to cursor.
            const range = editor.model.createRange(
                editor.model.createPositionAt(block, 0),
                pos
            )
            let text = ''
            for (const item of range.getItems()) {
                if (item.is && (item.is('$text') || item.is('$textProxy'))) {
                    text += item.data
                }
            }
            const atIdx = text.lastIndexOf('@')
            if (atIdx === -1) return null
            // Must be at start, or preceded by whitespace, to count as a mention.
            if (atIdx > 0 && !/\s/.test(text.charAt(atIdx - 1))) return null
            const between = text.substring(atIdx + 1)
            if (/\s/.test(between)) return null
            if (between.length > 30) return null
            return { atOffset: atIdx, query: between, block }
        },
        cursorScreenPosition() {
            const rect = this.cursorRect()
            if (!rect) {
                // Last resort: anchor against the editor itself.
                const el = this.editorInstance && this.editorInstance.ui
                    && this.editorInstance.ui.view && this.editorInstance.ui.view.element
                if (el) return this.clampDropdown(el.getBoundingClientRect())
                return { left: 0, top: 0 }
            }
            return this.clampDropdown(rect)
        },
        cursorRect() {
            // Try CKEditor's model→view→DOM mapping first.
            try {
                const editor = this.editorInstance
                const viewSel = editor.editing.view.document.selection
                const viewRange = viewSel.getFirstRange()
                if (viewRange) {
                    const domRange = editor.editing.view.domConverter.viewRangeToDom(viewRange)
                    if (domRange) {
                        const r = domRange.getBoundingClientRect()
                        if (r && (r.left || r.top || r.bottom)) return r
                    }
                }
            } catch (e) { /* fallthrough */ }
            // Fallback: the browser's own DOM selection (works for collapsed
            // ranges in most browsers and reflects the rendered position).
            try {
                const sel = window.getSelection && window.getSelection()
                if (sel && sel.rangeCount > 0) {
                    const r = sel.getRangeAt(0).getBoundingClientRect()
                    if (r && (r.left || r.top || r.bottom)) return r
                }
            } catch (e) { /* fallthrough */ }
            return null
        },
        clampDropdown(rect) {
            const vw = window.innerWidth
            const vh = window.innerHeight
            const dropdownWidth = 280
            const minHeight = 60
            const gap = 4
            const margin = 8
            // Horizontal: clamp inside the viewport.
            let left = rect.left
            if (left + dropdownWidth > vw - margin) left = vw - dropdownWidth - margin
            if (left < margin) left = margin
            // Vertical: anchor the *bottom* of the panel just above the cursor
            // so it grows upward with its actual content (no fixed-height gap
            // when the content is just "no match"). Fall back to anchor the
            // top below the cursor when there isn't enough room above.
            const spaceAbove = rect.top - margin
            if (spaceAbove >= minHeight) {
                return { left, bottom: vh - rect.top + gap }
            }
            return { left, top: rect.bottom + gap }
        },
        queueMentionSearch(query) {
            if (this.mentionSearchTimer) clearTimeout(this.mentionSearchTimer)
            this.mentionState.loading = true
            this.mentionSearchTimer = setTimeout(() => {
                this.fetchMentionUsers(query)
            }, 200)
        },
        async fetchMentionUsers(query) {
            const baseURL = this.$store.state.baseURL
            if (!baseURL) {
                this.mentionState.loading = false
                return
            }
            try {
                const res = await axios.get(`${baseURL}/users/find`, {
                    params: query ? { search: query } : {}
                })
                const raw = res.data && res.data.users
                const users = Array.isArray(raw) ? raw : (raw && raw.data) || []
                // Exclude citizens — only operators can be mentioned in internal notes.
                this.mentionState.users = users
                    .filter((u) => u.type !== 'citizen' && u.type !== 'citoyen')
                    .slice(0, 8)
                this.mentionState.selectedIndex = 0
            } catch (e) {
                this.mentionState.users = []
            } finally {
                this.mentionState.loading = false
            }
        },
        moveMentionSelection(delta) {
            const total = this.mentionState.users.length
            if (total === 0) return
            const next = (this.mentionState.selectedIndex + delta + total) % total
            this.mentionState.selectedIndex = next
        },
        onMentionHover(i) {
            this.mentionState.selectedIndex = i
        },
        selectMention(user) {
            const editor = this.editorInstance
            if (!editor || !user) return
            const block = this.mentionState.block
            const atOffset = this.mentionState.atOffset
            const displayName = user.name || user.email || `user-${user.id}`
            const mentionText = `@${displayName}`
            editor.model.change((writer) => {
                const start = writer.createPositionAt(block, atOffset)
                const end = editor.model.document.selection.getFirstPosition()
                writer.remove(writer.createRange(start, end))
                // Insert the mention as a single styled link — the href acts
                // as our marker and CKEditor preserves it through the model.
                writer.insertText(
                    mentionText,
                    { linkHref: `#mention-${user.id}` },
                    start
                )
                // Trailing space without the link attribute so the next
                // keystroke is plain text and the link doesn't keep growing.
                const afterMention = writer.createPositionAt(block, atOffset + mentionText.length)
                writer.insertText(' ', afterMention)
                writer.setSelection(
                    writer.createPositionAt(block, atOffset + mentionText.length + 1)
                )
                writer.removeSelectionAttribute('linkHref')
            })
            this.closeMentionDropdown()
            editor.editing.view.focus()
        },
        closeMentionDropdown() {
            this.mentionState.open = false
            this.mentionState.users = []
            this.mentionState.query = ''
            this.mentionState.selectedIndex = 0
        },
        extractMentions(html) {
            if (!html) return []
            const ids = new Set()
            // Pattern is the marker we control — won't match real user-typed links.
            const re = /href=["']#mention-(\d+)["']/g
            let m
            while ((m = re.exec(html)) !== null) {
                ids.add(parseInt(m[1], 10))
            }
            return Array.from(ids)
        },
        onChange() {
            if (
                this.content.includes('&lt;img') &&
                this.content.includes('&gt;')
            ) {
                let images = this.content.match(/&lt;img.*?&gt;/g)
                for (let i = 0; i < images.length; i++) {
                    this.content = this.content.replace(
                        images[i],
                        this.displayHtml(images[i])
                    )
                    const changeContent = new Promise((resolve, reject) => {
                        this.content = this.content.replace(
                            images[i],
                            this.displayHtml(images[i])
                        )
                        resolve()
                    })

                    changeContent.finally(() => {
                        const src = this.content.match(/src="(.*?)"/)[1]
                        const style = this.content.match(/style="(.*?)"/)
                            ? this.content.match(/style="(.*?)"/)[1]
                            : ''
                        const width = this.content.match(/width="(.*?)"/)
                            ? this.content.match(/width="(.*?)"/)[1]
                            : ''
                        const height = this.content.match(/height=(.*?)"/)
                            ? this.content.match(/height=(.*?)"/)[1]
                            : ''
                        let image = document.querySelector(
                            'img[src="' + src + '"]'
                        )
                        image.setAttribute(
                            'style',
                            style + ` width: ${width}; ` + `height: ${height};`
                        )
                    })
                }
            }
            if (
                this.content.includes('&lt;a') &&
                this.content.includes('&lt;/a&gt;')
            ) {
                let links = this.content.match(/&lt;a.*?&lt;\/a&gt;/g)
                for (let i = 0; i < links.length; i++) {
                    this.content = this.content.replace(
                        links[i],
                        this.displayHtml(links[i])
                    )
                }
            }
        },
        uploader(editor) {
            var token = window.localStorage.getItem('token')
            var baseURL = window.localStorage.getItem('baseURL')

            editor.plugins.get('FileRepository').createUploadAdapter = (
                loader
            ) => {
                return new UploadAdapter(loader, token, baseURL)
            }
        },
        postComment() {
            this.loading = true
            const mentions = this.extractMentions(this.content)
            if (this.filesList.length == 0) {
                this.sendMessage({ message: this.content, mentions })
            } else if (
                this.filesList.length > 0 &&
                (this.content == null || this.content == '')
            ) {
                this.sendMessage({
                    files: this.filesList
                })
            } else {
                this.sendMessage({
                    message: this.content,
                    files: this.filesList,
                    mentions
                })
            }
            this.filesList = []
            this.dropFiles = []
        },
        displayHtml(message, isText = false) {
            message = message
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&amp;/g, '&')
            if (!isText) message.replace(/<br\s*[\/]?>/gi, '')
            return message
        },
        sendMessage(content) {
            this.loading = true
            if (
                (this.content == '' || this.content == null) &&
                this.filesList == []
            ) {
                this.$store.dispatch('toast', {
                    type: 'error',
                    title: this.$t('error.title'),
                    message: this.$t('error.validation.not_empty')
                })
                this.loading = false
            } else {
                if (
                    this.content !== '' &&
                    this.content != null &&
                    this.content.match(/<a(.*?)<\/a>/)
                ) {
                    const regex = /<a href=".+">.{20,}<\/a>/g
                    content.message = content.message.replace(
                        regex,
                        (match) => {
                            return match.replace(/<a /, '<a class="long-url" ')
                        }
                    )
                }
                this.sendNewComment(content)
                    .then((response) => {
                        this.getPostHistories(this.post.id)
                        this.post.comments.push(response.data.comment)
                        this.content = ''
                        this.$store.dispatch('toast', {
                            type: 'success',
                            title: 'Sauvegarde effectuée'
                        })
                    })
                    .catch((error) => {
                        this.$store.dispatch('dispatchError', error)
                    })
                    .finally(() => {
                        this.loading = false
                    })
            }
        },
        showFiles(dropFiles) {
            dropFiles.forEach((file) => {
                this.encodeFileToBase64(file)
            })
        },
        onFileInput(event) {
            const files = Array.from(event.target.files || [])
            if (!files.length) return
            this.dropFiles = this.dropFiles.concat(files)
            this.showFiles(files)
            event.target.value = ''
        },
        encodeFileToBase64(file) {
            this.filesList = []
            var fr = new FileReader()
            fr.onload = (e) => {
                this.filesList.push({
                    name: file.name,
                    encodedFile: e.target.result
                })
            }
            fr.readAsDataURL(file)
        }
    }
}
</script>
